import React, { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AppContext } from '../../App';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { FaPlus, FaEdit, FaTrash, FaRocket, FaCheckCircle, FaClock, FaLock } from 'react-icons/fa';
import { useSubscriptionCheck } from '../../components/SubscriptionGate';

export default function MyProjects() {
  const { t, i18n } = useTranslation();
  const { API, user } = useContext(AppContext);
  const navigate = useNavigate();
  const { checkChantierLimit } = useSubscriptionCheck();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [formData, setFormData] = useState({
    project_name: '',
    location: '',
    site_address: '',
    delivery_deadline: '',
    additional_notes: '',
    supplies: []
  });
  const [newSupply, setNewSupply] = useState({
    name: '',
    quantity: 1,
    unit: 'pcs',
    description: '',
    specifications: ''
  });

  useEffect(() => {
    if (user) {
      fetchProjects();
    }
  }, [user]);

  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${API}/projects/my-projects?user_id=${user.id}`);
      setProjects(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching projects:', error);
      setLoading(false);
    }
  };

  const handleCreateProject = async (e) => {
    e.preventDefault();
    
    if (formData.supplies.length === 0) {
      alert(i18n.language === 'fr' ? 'Ajoutez au moins une fourniture' : 'Add at least one supply item');
      return;
    }

    try {
      await axios.post(`${API}/projects/create?user_id=${user.id}`, formData);
      setShowCreateModal(false);
      setFormData({
        project_name: '',
        location: '',
        site_address: '',
        delivery_deadline: '',
        additional_notes: '',
        supplies: []
      });
      fetchProjects();
    } catch (error) {
      console.error('Error creating project:', error);
      alert(i18n.language === 'fr' ? 'Erreur lors de la création' : 'Error creating project');
    }
  };

  const handleAddSupply = () => {
    if (!newSupply.name || newSupply.quantity <= 0) {
      alert(i18n.language === 'fr' ? 'Nom et quantité requis' : 'Name and quantity required');
      return;
    }

    setFormData({
      ...formData,
      supplies: [...formData.supplies, { ...newSupply }]
    });

    setNewSupply({
      name: '',
      quantity: 1,
      unit: 'pcs',
      description: '',
      specifications: ''
    });
  };

  const handleRemoveSupply = (index) => {
    setFormData({
      ...formData,
      supplies: formData.supplies.filter((_, i) => i !== index)
    });
  };

  const handlePublishProject = async (projectId) => {
    if (!confirm(i18n.language === 'fr' 
      ? 'Publier ce projet pour recevoir des devis ?' 
      : 'Publish this project to receive quotes?')) {
      return;
    }

    try {
      await axios.post(`${API}/projects/publish/${projectId}?user_id=${user.id}`);
      fetchProjects();
      alert(i18n.language === 'fr' ? 'Projet publié avec succès' : 'Project published successfully');
    } catch (error) {
      console.error('Error publishing project:', error);
      alert(i18n.language === 'fr' ? 'Erreur lors de la publication' : 'Error publishing project');
    }
  };

  const handleDeleteProject = async (projectId) => {
    // Basic browser confirmation (usually reliable)
    if (!window.confirm(i18n.language === 'fr' 
      ? 'Êtes-vous sûr de vouloir supprimer ce projet ?' 
      : 'Are you sure you want to delete this project?')) {
      return;
    }

    // Show loading state immediately
    const toastId = toast.loading(i18n.language === 'fr' ? 'Suppression en cours...' : 'Deleting...');

    try {
      console.log('Attempting to delete project:', projectId);
      await axios.delete(`${API}/projects/delete/${projectId}?user_id=${user.id}`);
      
      // Update UI immediately (Optimistic UI update not strictly needed if we refetch, but feels faster)
      setProjects(prev => prev.filter(p => p.id !== projectId));
      
      toast.success(i18n.language === 'fr' ? 'Projet supprimé avec succès' : 'Project deleted', {
        id: toastId
      });
      
      // Refetch to be sure
      fetchProjects();
    } catch (error) {
      console.error('Error deleting project:', error);
      toast.error(i18n.language === 'fr' ? 'Erreur lors de la suppression' : 'Error deleting project', {
        id: toastId
      });
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      draft: { color: 'bg-gray-200 text-gray-700', text: i18n.language === 'fr' ? 'Brouillon' : 'Draft' },
      published: { color: 'bg-blue-100 text-blue-700', text: i18n.language === 'fr' ? 'Publié' : 'Published' },
      quotes_received: { color: 'bg-[#635BFF]/10 text-[#5248d9]', text: i18n.language === 'fr' ? 'Devis reçus' : 'Quotes Received' },
      completed: { color: 'bg-purple-100 text-purple-700', text: i18n.language === 'fr' ? 'Terminé' : 'Completed' },
      cancelled: { color: 'bg-red-100 text-red-700', text: i18n.language === 'fr' ? 'Annulé' : 'Cancelled' }
    };

    const badge = badges[status] || badges.draft;
    return <span className={`px-3 py-1 rounded-full text-sm ${badge.color}`}>{badge.text}</span>;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F5F5F5] flex items-center justify-center">
        <p className="text-gray-600">{t('loading')}</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F5F5]">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-4xl font-bold text-[#0A2540] mb-2">
              {i18n.language === 'fr' ? 'Mes Projets de Chantier' : 'My Construction Projects'}
            </h1>
            <p className="text-gray-600">
              {i18n.language === 'fr' 
                ? 'Gérez vos projets et recevez des devis de fournisseurs' 
                : 'Manage your projects and receive supplier quotes'}
            </p>
          </div>
          <button
            onClick={async () => {
              // Check if user can create chantier/project
              const check = await checkChantierLimit();
              if (!check.allowed) {
                toast.error(check.message || 'Fonctionnalité non disponible', {
                  description: 'Les listes de chantier sont disponibles avec le pack Pro',
                  action: { label: 'Voir les offres', onClick: () => navigate('/pricing') }
                });
                return;
              }
              setShowCreateModal(true);
            }}
            className="bg-[#635BFF] text-white px-6 py-3 rounded-lg hover:bg-[#5248d9] flex items-center gap-2 transition"
          >
            <FaPlus />
            {i18n.language === 'fr' ? 'Nouveau Projet' : 'New Project'}
          </button>
        </div>

        {/* Projects List */}
        {projects.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <p className="text-gray-600 mb-4">
                {i18n.language === 'fr' 
                  ? 'Vous n\'avez pas encore de projet' 
                  : 'You don\'t have any projects yet'}
              </p>
              <button
                onClick={async () => {
                  const check = await checkChantierLimit();
                  if (!check.allowed) {
                    toast.error(check.message || 'Fonctionnalité non disponible', {
                      description: 'Les listes de chantier sont disponibles avec le pack Pro',
                      action: { label: 'Voir les offres', onClick: () => navigate('/pricing') }
                    });
                    return;
                  }
                  setShowCreateModal(true);
                }}
                className="bg-[#635BFF] text-white px-6 py-2 rounded-lg hover:bg-[#5248d9]"
              >
                {i18n.language === 'fr' ? 'Créer mon premier projet' : 'Create my first project'}
              </button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-6">
            {projects.map((project) => (
              <Card key={project.id} className="hover:shadow-lg transition">
                <CardContent className="p-6">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h3 className="text-xl font-bold text-[#0A2540] mb-2">{project.project_name}</h3>
                      <p className="text-gray-600 flex items-center gap-2">
                        📍 {project.location}
                        {project.site_address && ` - ${project.site_address}`}
                      </p>
                    </div>
                    {getStatusBadge(project.status)}
                  </div>

                  {/* Supplies Summary */}
                  <div className="mb-4">
                    <p className="text-sm text-gray-600 mb-2">
                      <strong>{i18n.language === 'fr' ? 'Fournitures:' : 'Supplies:'}</strong> {project.supplies?.length || 0}
                    </p>
                    {project.supplies && project.supplies.length > 0 && (
                      <div className="text-sm text-gray-600">
                        {project.supplies.slice(0, 3).map((supply, idx) => (
                          <span key={idx} className="inline-block bg-gray-100 px-2 py-1 rounded mr-2 mb-1">
                            {supply.name} ({supply.quantity} {supply.unit})
                          </span>
                        ))}
                        {project.supplies.length > 3 && (
                          <span className="text-gray-500">
                            +{project.supplies.length - 3} {i18n.language === 'fr' ? 'autres' : 'more'}
                          </span>
                        )}
                      </div>
                    )}
                  </div>

                  {project.delivery_deadline && (
                    <p className="text-sm text-gray-600 mb-4">
                      <FaClock className="inline mr-2" />
                      {i18n.language === 'fr' ? 'Échéance:' : 'Deadline:'} {project.delivery_deadline}
                    </p>
                  )}

                  {/* Actions */}
                  <div className="flex gap-3 flex-wrap">
                    <Link
                      to={`/user/projects/${project.id}`}
                      className="bg-[#0A2540] text-white px-4 py-2 rounded-lg hover:bg-[#003a52] text-sm"
                    >
                      {i18n.language === 'fr' ? 'Voir détails' : 'View Details'}
                    </Link>
                    
                    {project.status === 'draft' && (
                        <button
                          onClick={() => handlePublishProject(project.id)}
                          className="bg-[#635BFF] text-white px-4 py-2 rounded-lg hover:bg-[#5248d9] flex items-center gap-2 text-sm"
                        >
                          <FaRocket />
                          {i18n.language === 'fr' ? 'Publier' : 'Publish'}
                        </button>
                    )}

                    <button
                      onClick={(e) => {
                        e.stopPropagation(); // Stop navigation if clicking the button (though button is separate)
                        handleDeleteProject(project.id);
                      }}
                      className="bg-red-500 text-white px-4 py-2 rounded-lg hover:bg-red-600 flex items-center gap-2 text-sm"
                    >
                      <FaTrash />
                      {i18n.language === 'fr' ? 'Supprimer' : 'Delete'}
                    </button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Create Project Modal */}
        {showCreateModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <h2 className="text-2xl font-bold text-[#0A2540] mb-4">
                  {i18n.language === 'fr' ? 'Nouveau Projet de Chantier' : 'New Construction Project'}
                </h2>
                
                <form onSubmit={handleCreateProject}>
                  {/* Project Info */}
                  <div className="mb-4">
                    <label className="block text-sm font-semibold mb-2">
                      {i18n.language === 'fr' ? 'Nom du projet *' : 'Project Name *'}
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.project_name}
                      onChange={(e) => setFormData({...formData, project_name: e.target.value})}
                      className="w-full border border-gray-300 rounded-lg px-4 py-2"
                      placeholder={i18n.language === 'fr' ? 'Ex: Construction autoroute A10' : 'Ex: Highway A10 Construction'}
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-sm font-semibold mb-2">
                        {i18n.language === 'fr' ? 'Localisation *' : 'Location *'}
                      </label>
                      <input
                        type="text"
                        required
                        value={formData.location}
                        onChange={(e) => setFormData({...formData, location: e.target.value})}
                        className="w-full border border-gray-300 rounded-lg px-4 py-2"
                        placeholder={i18n.language === 'fr' ? 'Ex: Paris' : 'Ex: Paris'}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold mb-2">
                        {i18n.language === 'fr' ? 'Adresse du chantier' : 'Site Address'}
                      </label>
                      <input
                        type="text"
                        value={formData.site_address}
                        onChange={(e) => setFormData({...formData, site_address: e.target.value})}
                        className="w-full border border-gray-300 rounded-lg px-4 py-2"
                      />
                    </div>
                  </div>

                  <div className="mb-4">
                    <label className="block text-sm font-semibold mb-2">
                      {i18n.language === 'fr' ? 'Date limite de livraison' : 'Delivery Deadline'}
                    </label>
                    <input
                      type="text"
                      value={formData.delivery_deadline}
                      onChange={(e) => setFormData({...formData, delivery_deadline: e.target.value})}
                      className="w-full border border-gray-300 rounded-lg px-4 py-2"
                      placeholder={i18n.language === 'fr' ? 'Ex: 30 jours, 15 mars 2025' : 'Ex: 30 days, March 15, 2025'}
                    />
                  </div>

                  <div className="mb-4">
                    <label className="block text-sm font-semibold mb-2">
                      {i18n.language === 'fr' ? 'Notes additionnelles' : 'Additional Notes'}
                    </label>
                    <textarea
                      value={formData.additional_notes}
                      onChange={(e) => setFormData({...formData, additional_notes: e.target.value})}
                      className="w-full border border-gray-300 rounded-lg px-4 py-2"
                      rows="3"
                    />
                  </div>

                  {/* Supplies Section */}
                  <div className="mb-6">
                    <h3 className="text-lg font-bold text-[#0A2540] mb-3">
                      {i18n.language === 'fr' ? 'Fournitures nécessaires *' : 'Required Supplies *'}
                    </h3>
                    
                    {/* Added Supplies List */}
                    {formData.supplies.length > 0 && (
                      <div className="mb-4 space-y-2">
                        {formData.supplies.map((supply, index) => (
                          <div key={index} className="bg-gray-50 p-3 rounded-lg flex justify-between items-center">
                            <div>
                              <p className="font-semibold">{supply.name}</p>
                              <p className="text-sm text-gray-600">
                                {supply.quantity} {supply.unit}
                                {supply.description && ` - ${supply.description}`}
                              </p>
                            </div>
                            <button
                              type="button"
                              onClick={() => handleRemoveSupply(index)}
                              className="text-red-500 hover:text-red-700"
                            >
                              <FaTrash />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Add Supply Form */}
                    <div className="bg-blue-50 p-4 rounded-lg">
                      <div className="grid grid-cols-3 gap-3 mb-3">
                        <div className="col-span-2">
                          <input
                            type="text"
                            value={newSupply.name}
                            onChange={(e) => setNewSupply({...newSupply, name: e.target.value})}
                            className="w-full border border-gray-300 rounded-lg px-3 py-2"
                            placeholder={i18n.language === 'fr' ? 'Nom de la fourniture' : 'Supply name'}
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <input
                            type="number"
                            min="1"
                            value={newSupply.quantity}
                            onChange={(e) => setNewSupply({...newSupply, quantity: parseInt(e.target.value)})}
                            className="w-full border border-gray-300 rounded-lg px-3 py-2"
                            placeholder="Qté"
                          />
                          <select
                            value={newSupply.unit}
                            onChange={(e) => setNewSupply({...newSupply, unit: e.target.value})}
                            className="w-full border border-gray-300 rounded-lg px-3 py-2"
                          >
                            <option value="pcs">pcs</option>
                            <option value="m3">m³</option>
                            <option value="tons">tonnes</option>
                            <option value="kg">kg</option>
                            <option value="m">m</option>
                            <option value="m2">m²</option>
                          </select>
                        </div>
                      </div>
                      <div className="mb-3">
                        <input
                          type="text"
                          value={newSupply.description}
                          onChange={(e) => setNewSupply({...newSupply, description: e.target.value})}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2"
                          placeholder={i18n.language === 'fr' ? 'Description (optionnel)' : 'Description (optional)'}
                        />
                      </div>
                      <button
                        type="button"
                        onClick={handleAddSupply}
                        className="w-full bg-[#635BFF] text-white px-4 py-2 rounded-lg hover:bg-[#5248d9] flex items-center justify-center gap-2"
                      >
                        <FaPlus />
                        {i18n.language === 'fr' ? 'Ajouter cette fourniture' : 'Add this supply'}
                      </button>
                    </div>
                  </div>

                  {/* Form Actions */}
                  <div className="flex gap-3">
                    <button
                      type="submit"
                      className="flex-1 bg-[#0A2540] text-white px-6 py-3 rounded-lg hover:bg-[#003a52] font-semibold"
                    >
                      {i18n.language === 'fr' ? 'Créer le projet' : 'Create Project'}
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowCreateModal(false)}
                      className="px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-100"
                    >
                      {i18n.language === 'fr' ? 'Annuler' : 'Cancel'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
